<script setup lang="ts">
import {
  DataPointDetailsDto,
  instanceOfEnumerationEvaluationResultDto,
  instanceOfManualTaskWorkResultDto,
  instanceOfNumericEvaluationResultDto,
  ManualTaskWorkResultDto,
} from '@/api/generator';
import { computed } from 'vue';
import { mdiPlus } from '@mdi/js';
import { edit } from '../icons/icons';
import { fullName } from '@/utils/helper';
import DateTimeFormatter from '../formatters/DateTimeFormatter.vue';
import EvaluationState from '../evaluation/EvaluationState.vue';
import { toDisplayValue } from '@/utils/numberUtils';

const props = defineProps<{
  value: ManualTaskWorkResultDto | DataPointDetailsDto;
}>();

const history = computed(() => props.value.history?.toReversed());

const hasHistory = computed(() => !!history.value?.length);

const evaluationResult = computed(() => props.value.evaluationResult);

const disableEvaluationState = computed(() => {
  if (instanceOfManualTaskWorkResultDto(props.value)) {
    return props.value.state === 'Ignored';
  } else {
    return false;
  }
});

const userInfo = computed(() => {
  const isModified = !!evaluationResult.value?.lastModifierUser && hasHistory.value;

  if (isModified) {
    return {
      time: evaluationResult.value?.lastModificationTime ?? undefined,
      user: evaluationResult.value.lastModifierUser ?? undefined,
      icon: edit,
    };
  } else {
    return {
      time: evaluationResult.value?.creationTime ?? undefined,
      user: evaluationResult.value?.creatorUser ?? undefined,
      icon: mdiPlus,
    };
  }
});
</script>

<template>
  <v-expansion-panels
    flat
    :rounded="false"
    :hideActions="!hasHistory"
    :readonly="!hasHistory"
    bgColor="#00000000"
  >
    <v-expansion-panel static>
      <v-expansion-panel-title class="justify-end panelTitle">
        <v-icon :icon="userInfo.icon" class="mr-2" />

        <DateTimeFormatter class="text-body-1" :value="userInfo.time" />
        |
        <span v-if="userInfo.user" class="text-body-1 mr-3">
          {{ fullName(userInfo.user) }}
        </span>
      </v-expansion-panel-title>

      <v-expansion-panel-text>
        <div
          v-for="historyEntry in history"
          :key="historyEntry.timestamp"
          class="d-flex align-center justify-end mb-2"
        >
          <v-icon :icon="historyEntry === history?.at(-1) ? mdiPlus : edit" class="mr-2" />

          <DateTimeFormatter class="text-body-1" :value="historyEntry.timestamp" />
          |
          <span v-if="historyEntry.user" class="text-body-1">
            {{ fullName(historyEntry.user) }}
          </span>

          <span v-if="evaluationResult" class="ml-2">
            <span v-if="instanceOfEnumerationEvaluationResultDto(evaluationResult)">
              {{ historyEntry.enumerationOptionName }}
            </span>

            <span
              v-if="instanceOfNumericEvaluationResultDto(evaluationResult) && historyEntry.value"
            >
              {{
                toDisplayValue(historyEntry.value, {
                  fractionDigits: evaluationResult.decimalPlaces,
                  unitAbbreviation: evaluationResult?.deviationUnit,
                })
              }}
            </span>
          </span>

          <EvaluationState
            class="ml-2"
            :value="historyEntry.output || undefined"
            :disabled="disableEvaluationState"
          />
        </div>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<style scoped lang="scss">
.v-expansion-panel :deep(.v-expansion-panel-title) {
  border-bottom: 0;
  font-size: unset;

  &:hover > .v-expansion-panel-title__overlay {
    opacity: 0;
  }
}

.panelTitle {
  justify-self: flex-end;
  width: auto;
}
</style>
